import type { ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { useAppDispatch } from '@/store/hooks'
import { useGoToLessonSettings } from '@/features/editor/components/studio/useLessonLastBlock'
import {
  selectBlock,
  selectLesson,
  setDialogOpen,
  setSettingsLang,
  setSettingsTab,
  setContentLang,
} from '@/features/editor/editorSlice'
import type { ViolationGo } from '../publish/violationPlace'
import { useMessages } from '@/i18n'
import { messages } from './messages'

export const GoButton = ({ onClick, label }: { onClick: () => void; label: string }) => {
  const m = useMessages(messages)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      // Фирменный фиолетовый, как у остальных главных кнопок студии (решение заказчика).
      className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-brand px-2.5 py-1 text-xs font-semibold text-brand-foreground transition-colors hover:bg-brand-hover"
    >
      {/* На телефоне текст не влезал: кнопка уезжала за край таблицы. Подпись — в aria-label. */}
      <span className="hidden md:inline">{m.go}</span>
      <ChevronRight className="size-3.5" />
    </button>
  )
}

/** Таблица с рамкой: шапка и строки, разделённые линией. */
export type IssuesTone = 'inverted' | 'surface'

/** Цвета таблицы: в светлом облачке (`inverted`) и на тёмной панели (`surface`). */
const TONE: Record<IssuesTone, { box: string; head: string; rows: string }> = {
  inverted: {
    box: 'border-background/15',
    head: 'bg-background/5',
    rows: '[&>tr]:border-background/10',
  },
  surface: { box: 'border-border', head: 'bg-muted/40', rows: '[&>tr]:border-border' },
}

export const Table = ({
  head,
  children,
  tone = 'inverted',
}: {
  head: ReactNode
  children: ReactNode
  tone?: IssuesTone
}) => (
  <div className={`overflow-hidden rounded-xl border ${TONE[tone].box}`}>
    <table className="w-full border-collapse">
      <thead className={`text-left text-xs ${TONE[tone].head}`}>{head}</thead>
      <tbody className={`[&>tr]:border-t ${TONE[tone].rows}`}>{children}</tbody>
    </table>
  </div>
)

export const TH = 'px-1.5 py-1.5 font-medium opacity-70 first:pl-3 md:px-3'
export const TD = 'px-1.5 py-1.5 align-middle first:pl-3 last:pr-3 md:px-3'

/** Переход к месту проблемы: свой (`onGo`, настройки курса) или в редакторе. */
export const useIssueNavigation = (onGo?: (target: ViolationGo) => void) => {
  const dispatch = useAppDispatch()
  const goToLessonSettings = useGoToLessonSettings()
  return (target: ViolationGo) => {
    if (onGo) return onGo(target)
    if (target.kind === 'course') {
      dispatch(setSettingsLang(target.lang ?? null))
      // Вкладку — до открытия: диалог читает её при каждом открытии.
      if (target.tab) dispatch(setSettingsTab(target.tab))
      dispatch(setDialogOpen({ dialog: 'settingsOpen', open: true }))
      return
    }
    // Незаполненное поле урока или блока на другом языке — правка переключается на него.
    if (target.lang) dispatch(setContentLang(target.lang))
    // selectLesson гасит выбранный блок — блок выбираем ПОСЛЕ него.
    // «Перейти» к уроку (не к блоку) — в настройки урока, а не на его экран.
    if (target.kind === 'lesson') {
      goToLessonSettings(target.lessonId)
      return
    }
    dispatch(selectLesson(target.lessonId))
    dispatch(selectBlock(target.blockId))
  }
}
